/*
* Created on Jan 11, 2021
* @author: Adam Cheng <adamcheng@qnap.com>
*/
import { useOrganization, useOrganizationUpdate } from '../context/OrganizationContext'
import {NavLink, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import {useOrganizationApi} from "../hook/OrganizationAPIHook";

const MainNavigationComponent = ({}) => {
    const {t, i18n} = useTranslation();
    // Subscribe to route changes: getCurrentOrg() reads window.location, so
    // without this the nav wouldn't re-render on back/forward or NavLink moves.
    useLocation();
    const {getCurrentOrg, setCurrentOrg, userProfile} = useOrganization();
    const {is_owner, role, hasDevicePermission} = userProfile();
    const isMember = role === 'member';
    const {OrganizationAPI} = useOrganizationApi();
    const organization = getCurrentOrg();
    const path = organization ? `/${organization.organization_id}` : ''
    const [collapsed, setCollapsed] = useState('off')

    const is404 = () => {
        return window.location.pathname === '/404_page_not_found'
    }

    useEffect(() => {
        // Refetch when currentOrg is missing OR when it's the slim shape
        // written by the org selector (no users[]). The selector list
        // endpoint returns only {organization_id, name, default?}; downstream
        // per-org pages (Groups/AccountList/Devices) read users[]/groups[]
        // from currentOrg, so we upgrade to the fat shape from
        // /organization/<id> here.
        const needsFetch = !organization || !organization.users;
        if (needsFetch) {
            // CMNQ2CST-492: don't parse useLocation() here — react-router v7
            // flushes location state in a transition, so on the render this
            // effect fires from it can still hold the pre-navigation path
            // (org_id '' → fetch skipped → nav stuck on the slim member
            // view). currentOrg's id is already URL-validated by
            // getCurrentOrg(); fall back to window.location (the clock
            // getCurrentOrg reads) only when there is no stored org.
            const path = window.location.pathname.split('/');
            const org_id = organization?.organization_id || path?.[1] || '';
            if (org_id !== '' && !org_id.startsWith('404') && !org_id.startsWith('delete-org-confirm')) {
                OrganizationAPI.get(`organization/${org_id}`, {lang: i18n.language}).then(resp => {
                    setCurrentOrg(resp.result);
                });
            }
        }
    }, [organization?.organization_id])

    return (
        <nav className={`main-nav  ${organization && window.location.pathname.length > 1 && !is404() ? '' : 'hide'}`} data-nav-collapsed={collapsed}>
            <div className="mainnav-row">
                <div className="mainnav-category">
                    <div className="category-label">{`${t('@Organization Information@')}`}</div>
                </div>
                <NavLink className="mainnav-item" to={`${path}/settings`}>
                    <div className="mainnav-item-group">
                        <div className="mainnav-icon mainnav-profile"/>
                        <div className="mainnav-label">{t('@Organization Profile@')}</div>
                    </div>
                </NavLink>
                <NavLink className="mainnav-item" to={`${path}/sites`}>
                    <div className="mainnav-item-group">
                        <div className="mainnav-icon mainnav-site"/>
                        <div className="mainnav-label">{t('@Sites@')}</div>
                    </div>
                </NavLink>
                {!isMember &&
                    <NavLink className="mainnav-item" to={`${path}/group`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-group"/>
                            <div className="mainnav-label">{t('@Groups@')}</div>
                        </div>
                    </NavLink>
                }
                {!isMember &&
                    <NavLink className="mainnav-item" to={`${path}/activity-log`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-activity"/>
                            <div className="mainnav-label">{t('@Activity log@')}</div>
                        </div>
                    </NavLink>
                }

                {!isMember &&
                    <div className="mainnav-category">
                        <div className="category-label">{`${t('@User@')} ${t('@Management@')}`}</div>
                    </div>
                }
                {!isMember &&
                    <NavLink className="mainnav-item" to={`${path}/account-list`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-contacts"/>
                            <div className="mainnav-label">{t('@Account list@')}</div>
                        </div>
                    </NavLink>
                }
                {!isMember && window._env_.REACT_APP_ENABLE_SSO === 'true' &&
                    <NavLink className="mainnav-item" to={`${path}/security`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-security"/>
                            <div className="mainnav-label">{t('@Security@')}</div>
                        </div>
                    </NavLink>
                }

                {(!isMember || hasDevicePermission) &&
                    <div className="mainnav-category">
                        <div className="category-label">{t('@Resources@')}</div>
                    </div>
                }
                {(!isMember || hasDevicePermission) &&
                    <NavLink className="mainnav-item" to={`${path}/devices`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-device"/>
                            <div className="mainnav-label">{t('@Device@')}</div>
                        </div>
                    </NavLink>
                }
                {is_owner &&
                    <NavLink className="mainnav-item" to={`${path}/cloud-space`}>
                        <div className="mainnav-item-group">
                            <div className="mainnav-icon mainnav-cloud-space"/>
                            <div className="mainnav-label">{t('@Cloud Space@')}</div>
                        </div>
                    </NavLink>
                }
            </div>

            <div className="mainnav-row">
                <a className="mainnav-item">
                <div className="mainnav-item-group" onClick={()=>setCollapsed(prev=> prev==='off' ? 'on' : 'off')}>
                        <div className="mainnav-icon mainnav-collapsed"></div>
                        <div className="mainnav-label">{t('@Collapse@')}</div>
                    </div>
                </a>
            </div>
        </nav>
    );
};

export default MainNavigationComponent;